import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { formatDate } from '@/shared/lib/dateUtils'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect } from "react";
import {
  Users,
  X,
  CirclePlus,
  FileText,
  Save,
  Trash,
  Pen,
  Loader2,
} from "lucide-react";
import UserAssignmentTable from '@/features/configuracion/components/UserAssignmentTable';
import "./AdminToggleStyles.css";
import { config } from '@/config/env';
import { SwitchCustom } from '@/shared/components/Switch';
import { userHasPermission } from '@/shared/lib/roleUtils';
import ConfirmActionModal from '@/shared/components/ConfirmActionModal';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import { useAuth } from '@/shared/hooks/useAuth';
import toast from "react-hot-toast";
import { filterOutMasterUsers } from '@/shared/lib/userFilters';
import { EsqueletoTabla } from "@/shared/components/Esqueleto";

interface Project {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  // Los dos estados que existen en el servidor. El interruptor de la tabla
  // alterna entre ellos; no hay más.
  status: "active" | "paused";
  moduleId?: string;
  createdAt: string;
  updatedAt: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
}

interface UserAssignment {
  userId: string;
  projectId: string;
  permissions: string[];
  isActive: boolean;
}

interface ProjectUsersResponse {
  assignedUsers: User[];
  unassignedUsers: User[];
  assignments: UserAssignment[];
}

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (projectData: {
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    status: string;
    moduleId?: string;
  }) => void;
  loading: boolean;
  proyectData?: Project;
  onEdit?: (projectData: {
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    status: string;
    moduleId?: string;
  }) => void;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  proyectData,
  onEdit,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  useEffect(() => {
    if (proyectData) {
      setFormData({
        name: proyectData.name,
        description: proyectData.description,
      });
    }
  }, [proyectData, isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && !proyectData) {
      setFormData({
        name: "",
        description: "",
      });
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (proyectData) {
      await onEdit?.({ ...formData, projectId: proyectData?.id } as any);
    } else {
      await onSubmit(formData as any);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (!isOpen) return null;

  return (
    <CapaDeModal>
      <div className="bg-[#ffffff] rounded-lg shadow-xl max-w-xl w-full mx-4">
        <div className="flex items-center justify-between p-6">
          <h3 className="text-xl font-semibold text-[#1E1E1F]">
            {proyectData ? "Editar Proyecto" : "Crear Nuevo Proyecto"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 h-[24px] w-[24px] bg-[#D9D9D9] rounded-full p-1 hover:bg-[#D9D9D9]/60"
            disabled={loading}
          >
            <X className="h-4 w-4" color="#7e7e94ff" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Nombre del Proyecto *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border-0 rounded-md bg-[#F6F9FB]"
              placeholder="Ej: Implementación Sistema ERP"
            />
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Descripción *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={3}
              className="w-full px-3 py-2 border-0 rounded-md bg-[#F6F9FB]"
              placeholder="Describe los objetivos del proyecto..."
            />
          </div>
          <div className="bg-[#E8F8FD] py-3 px-4 rounded-md flex">
            <div className="h-[24px] w-auto mr-3">
              <FileText color="#116583" />
            </div>
            <p className="text-sm text-[#116583]">
              <strong>Nota:</strong> El proyecto se creará con estado "activo"
              por defecto. Las fechas, módulo y otros detalles se pueden
              configurar después de la creación.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={estiloDeBoton('secundario')}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={
                loading || !formData.name.trim() || !formData.description.trim()
              }
              className={estiloDeBoton('primario')}
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <Save className="h-4 w-4" />
              <span>
                {loading
                  ? proyectData
                    ? "Actualizando..."
                    : "Creando..."
                  : proyectData
                    ? "Actualizar Proyecto"
                    : "Crear Proyecto"}
              </span>
            </button>
          </div>
        </form>
      </div>
    </CapaDeModal>
  );
};

const ProjectsView: React.FC = () => {
  const { user } = useAuth();

  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [showAssignments, setShowAssignments] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Id del proyecto que se esta cargando para editar. Es por fila y no un
  // booleano de pantalla: con el booleano giraban los iconos de todas las filas,
  // y antes de eso desaparecia el listado entero.
  const [proyectoCargandoId, setProyectoCargandoId] = useState<string | null>(null);
  // Antes un fallo al eliminar solo se escribia en la consola.
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Proyecto que se va a eliminar, pendiente de confirmar. Antes el boton
  // borraba al primer clic, sin preguntar.
  const [proyectoAEliminar, setProyectoAEliminar] = useState<Project | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [creating, setCreating] = useState(false);
  const [projectData, setProjectData] = useState<Project>();

  // Cargar proyectos al montar el componente
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${config.apiBaseUrl}/projects`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      } else {
        console.error("Error fetching projects:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectsById = async (id: string) => {
    try {
      setProyectoCargandoId(id);
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${config.apiBaseUrl}/projects/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProjectData(data);
      } else {
        console.error("Error fetching projects:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setProyectoCargandoId(null);
    }
  };

  const deleteProjects = async (id: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${config.apiBaseUrl}/projects/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        // El back responde sin cuerpo, asi que aqui NO se puede leer json():
        // reventaba con "Unexpected end of JSON input", caia al catch y nunca
        // se refrescaba. El proyecto si se borraba, pero seguia en pantalla y
        // parecia que el boton no hacia nada.
        await fetchProjects();
      } else {
        const mensaje = await response
          .json()
          .then((d) => d?.message)
          .catch(() => null);
        setDeleteError(mensaje || "No se pudo eliminar el proyecto.");
      }
    } catch (error) {
      console.error("Error al eliminar el proyecto:", error);
      setDeleteError("No se pudo eliminar el proyecto.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectAssignments = async (projectId: string) => {
    try {
      setAssignmentLoading(true);
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${config.apiBaseUrl}/projects/${projectId}/users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const data: ProjectUsersResponse = await response.json();
        const mergedUsers = [
          ...(data.assignedUsers || []),
          ...(data.unassignedUsers || []),
        ];
        setUsers(filterOutMasterUsers(mergedUsers));
        const normalizedAssignments = (data.assignments || []).map(
          (assignment) => ({
            ...assignment,
            permissions: assignment.permissions || [],
          }),
        );
        setUserAssignments(normalizedAssignments);
      } else {
        console.error(
          "Error fetching project assignments:",
          response.statusText,
        );
        setUsers([]);
        setUserAssignments([]);
      }
    } catch (error) {
      console.error("Error fetching project assignments:", error);
      setUsers([]);
      setUserAssignments([]);
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setShowAssignments(true);
    fetchProjectAssignments(project.id);
  };

  const handleAssignUser = async (userId: string) => {
    if (!selectedProject) return;

    try {
      const token = localStorage.getItem("access_token");

      const response = await fetch(
        `${config.apiBaseUrl}/user-assignments/projects`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId,
            projectId: selectedProject.id,
          }),
        },
      );

      if (response.ok) {
        fetchProjectAssignments(selectedProject.id);
        toast.success("Usuario asignado exitosamente al proyecto");
      } else {
        const errorData = await response.text();
        console.error("Error assigning user:", response.statusText, errorData);
        toast.error(`Error al asignar usuario: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error assigning user:", error);
      toast.error(`Error de conexión: ${error}`);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!selectedProject) return;

    try {
      const token = localStorage.getItem("access_token");

      // Using the working PATCH endpoint for user removal
      const response = await fetch(
        `${config.apiBaseUrl}/users/${userId}/projects/${selectedProject.id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isActive: false }),
        },
      );

      if (response.ok) {
        fetchProjectAssignments(selectedProject.id);
        toast.success("Usuario removido exitosamente del proyecto");
      } else {
        const errorData = await response.text();
        console.error("Error removing user:", response.statusText, errorData);
        toast.error(`Error al remover usuario: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error removing user:", error);
      toast.error(`Error de conexión: ${error}`);
    }
  };

  const handleBackToProjects = () => {
    setShowAssignments(false);
    setSelectedProject(null);
    setUserAssignments([]);
  };

  const handleProjectToggle = async (
    projectId: string,
    isCurrentlyActive: boolean,
  ) => {
    try {
      const token = localStorage.getItem("access_token");
      const newStatus = isCurrentlyActive ? "paused" : "active";

      const response = await fetch(
        `${config.apiBaseUrl}/projects/${projectId}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: newStatus }),
        },
      );

      if (response.ok) {
        fetchProjects(); // Refresh the projects list
        toast.success(
          `Proyecto ${
            newStatus === "active" ? "activado" : "pausado"
          } exitosamente`,
        );
      } else {
        // `statusText` viene vacío en HTTP/2, y el aviso quedaba como
        // "Error al cambiar el estado del proyecto:" sin nada después. El
        // motivo real viene en el cuerpo.
        const detalle = await response
          .json()
          .then((d) => (Array.isArray(d?.message) ? d.message.join(" ") : d?.message))
          .catch(() => null);
        console.error("Error updating project status:", response.status, detalle);
        toast.error(
          detalle
            ? `No se pudo cambiar el estado del proyecto: ${detalle}`
            : `No se pudo cambiar el estado del proyecto (error ${response.status})`,
        );
      }
    } catch (error) {
      console.error("Error toggling project status:", error);
      toast.error(`Error de conexión: ${error}`);
    }
  };

  const handleCreateProject = async (projectData: {
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    status: string;
    moduleId?: string;
  }) => {
    try {
      setCreating(true);
      const token = localStorage.getItem("access_token");

      // Solo enviar los campos que acepta el backend
      const payload = {
        name: projectData.name,
        description: projectData.description,
      };

      const response = await fetch(`${config.apiBaseUrl}/projects`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const newProject = await response.json();
        setProjects((prev) => [...prev, newProject]);
        setShowCreateModal(false);
      } else {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = await response.text();
        }
        console.error("Error creating project:", {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
        });
        toast.error(
          `Error al crear proyecto: ${errorData.message || response.statusText}`,
        );
      }
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error(`Network error: ${error}`);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateProject = async (projectData: {
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    status: string;
    moduleId?: string;
    projectId?: string;
  }) => {
    try {
      setCreating(true);
      const token = localStorage.getItem("access_token");

      // Solo enviar los campos que acepta el backend
      const payload = {
        name: projectData.name,
        description: projectData.description,
      };

      const response = await fetch(
        `${config.apiBaseUrl}/projects/${projectData.projectId}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (response.ok) {
        window.location.reload();
      } else {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = await response.text();
        }
        console.error("Error updating project:", {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
        });
        toast.error(
          `Error al editar proyecto: ${errorData.message || response.statusText}`,
        );
      }
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error(`Network error: ${error}`);
    } finally {
      setCreating(false);
    }
  };

  // A-01: silueta de la tabla de proyectos.
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
        <EsqueletoTabla filas={6} columnas={4} />
      </div>
    );
  }

  if (
    userHasPermission(
      "generales.catalogos.proyectos.read",
      user?.rbac?.effectivePermissions || [],
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div>
      {deleteError && (
        <div className="flex items-start justify-between gap-3 px-4 py-3 mb-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{deleteError}</p>
          <button
            type="button"
            onClick={() => setDeleteError(null)}
            className="text-sm font-medium text-red-700 hover:underline"
          >
            Cerrar
          </button>
        </div>
      )}
      {!showAssignments ? (
        <>
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center">
              <TituloDePantalla nivel="interna">
                Gestión de Proyectos
              </TituloDePantalla>
            </div>
            {userHasPermission(
              "generales.catalogos.proyectos.create",
              user?.rbac?.effectivePermissions || [],
            ) && (
              <button
                onClick={() => setShowCreateModal(true)}
                className={estiloDeBoton('primario')}
              >
                <CirclePlus className="h-4 w-4" />
                <p className="text-sm">Nuevo Proyecto</p>
              </button>
            )}
          </div>

          {/* Projects Table */}
          <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E5E7EB]">
              <thead className="bg-[#FAF9FF]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Lista de proyectos
                  </th>
                  {/* <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Fechas
                  </th> */}
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Fecha Creación
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E1E1E1]">
                {projects.map((project) => (
                  <tr key={project.id} className="hover:bg-[#F4F7FB] transition-colors">
                    <td
                      className="px-6 py-4 align-top cursor-pointer"
                      onClick={() => handleProjectSelect(project)}
                    >
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900 break-words">
                          {project.name}
                        </div>
                      </div>
                    </td>
                    {/* <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => handleProjectSelect(project)}
                    >
                      <div className="flex flex-col text-sm">
                        <div className="font-semibold">
                          {project?.startDate
                            ? formatDate(project.startDate)
                            : "--/--/----"}
                        </div>
                        <div className="text-xs text-[#959595]">
                          hasta{" "}
                          {project?.endDate
                            ? formatDate(project.endDate)
                            : "--/--/----"}
                        </div>
                      </div>
                    </td> */}
                    <td
                      className="px-6 py-4 whitespace-nowrap align-top cursor-pointer"
                      onClick={() => handleProjectSelect(project)}
                    >
                      <div className="flex items-center">
                        <SwitchCustom
                          //value={project.status === "active"}
                          value={true}
                          disabled
                          onChange={() => {
                            handleProjectToggle(
                              project.id,
                              project.status === "active",
                            );
                          }}
                        />
                        <p className="ml-2 text-sm">
                          {/* {project.status === "active" ? "Activo" : "Inactivo"} */ "Activo"}
                        </p>
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top cursor-pointer"
                      onClick={() => handleProjectSelect(project)}
                    >
                      {project.createdAt
                        ? formatDate(project.createdAt)
                        : "--/--/----"}
                    </td>
                    <td className=" py-4 text-sm font-medium flex justify-start">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleProjectSelect(project)}
                          className="text-[#315FB9] hover:text-[#315FB9]/80 hover:underline inline-flex items-center space-x-1 text-sm font-semibold"
                        >
                          <Users className="h-4 w-4" />
                          <span>Usuarios</span>
                        </button>
                        <button
                          disabled={proyectoCargandoId !== null}
                          onClick={async () => {
                            await fetchProjectsById(project.id);
                            setShowCreateModal(true);
                          }}
                          className="text-[#315FB9] hover:text-[#315FB9]/80 hover:underline inline-flex items-center space-x-1 text-sm font-semibold ml-2 disabled:opacity-50"
                        >
                          {proyectoCargandoId === project.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Pen className="h-4 w-4" />
                          )}
                          <span>Editar</span>
                        </button>
                        {userHasPermission(
                          "generales.catalogos.proyectos.delete",
                          user?.rbac?.effectivePermissions || [],
                        ) && (
                          <button
                            onClick={() => setProyectoAEliminar(project)}
                            className="text-[#EF4444] hover:text-[#EF4444]/80 hover:underline inline-flex items-center space-x-1 text-sm font-semibold ml-2"
                          >
                            <Trash className="h-4 w-4" />
                            <span>Eliminar</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </>
      ) : (
        <UserAssignmentTable
          title={`Usuarios del Proyecto: ${selectedProject?.name}`}
          users={users}
          assignments={userAssignments}
          loading={assignmentLoading}
          onAssign={handleAssignUser}
          onRemove={handleRemoveUser}
          onBack={handleBackToProjects}
          type="project"
        />
      )}

      <ConfirmActionModal
        isOpen={proyectoAEliminar !== null}
        title="Eliminar proyecto"
        message={`¿Seguro que quieres eliminar ${proyectoAEliminar?.name ?? "este proyecto"}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variante="peligro"
        isLoading={eliminando}
        onConfirm={async () => {
          if (!proyectoAEliminar) return;
          setEliminando(true);
          await deleteProjects(proyectoAEliminar.id);
          setEliminando(false);
          setProyectoAEliminar(null);
        }}
        onCancel={() => {
          if (eliminando) return;
          setProyectoAEliminar(null);
        }}
      />

      {/* Create Project Modal */}
      <CreateProjectModal
        proyectData={projectData}
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setProjectData(undefined);
        }}
        onSubmit={handleCreateProject}
        loading={creating}
        onEdit={handleUpdateProject}
      />
    </div>
  );
};

export default ProjectsView;
